import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  useVendor, 
  useVerifyVendor, 
  useRejectVendor,
  useUpdateVendorProfile,
  useSuspendVendor
} from '../../services/vendorService';
import VendorHeader from '../../components/admin/VendorHeader';
import OverviewTab from '../../components/admin/vendor/OverviewTab';
import ServicesCategoryTab from '../../components/admin/vendor/ServicesCategoryTab';
import ServicesTab from '../../components/admin/vendor/ServicesTab';
import PackagesTab from '../../components/admin/vendor/PackagesTab';
import PortfolioTab from '../../components/admin/vendor/PortfolioTab';
import VerificationTab from '../../components/admin/vendor/VerificationTab';
import AnalyticsTab from '../../components/admin/vendor/AnalyticsTab';
import OrdersTab from '../../components/admin/vendor/OrdersTab';
import {
  ArrowLeft,
  XCircle,
  Star,
  Camera,
  Shield,
  DollarSign,
  FileText,
  Eye,
  Package,
  Gift,
  ShoppingBag,
  ListChecks,
  Loader2,
  Banknote,
  BookOpen,
  CreditCard,
  Trash2,
} from 'lucide-react';
import { SearchSelect, Button } from '../../components/shared';
import {  StatsGrid } from '../../components/admin';
import { useToastContext } from '../../contexts/ToastContext';
import VendorDocumentsHub from '../../components/admin/vendor/VendorDocumentsHub';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { postAdminVendorConnectDisconnect } from '../../services/vendorPayoutService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import StripeConnectStatusBadge from '../../components/shared/StripeConnectStatusBadge';

const VENDOR_DETAIL_TABS = [
  'overview',
  'verification',
  'services',
  'packages',
  'services_category',
  'portfolio',
  'orders',
  'analytics',
];

const AdminVendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const activeTab = VENDOR_DETAIL_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview';
  const setActiveTab = (tabId) => {
    const next = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', tabId);
    }
    setSearchParams(next, { replace: true });
  };

  // Utility function for readable date formatting
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const { success, error: showError } = useToastContext();
  const [verificationData, setVerificationData] = useState({
    verification_level: 'verified',
    verification_notes: ''
  });
  const [rejectionData, setRejectionData] = useState({
    rejection_reason: '',
    rejection_type: 'other'
  });
  // React Query hooks
  const { 
    data: vendorData, 
    isLoading: loading, 
    error: vendorError,
    refetch: refetchVendor
  } = useVendor(id);

  // console.log(vendorData, 'vendorData');
  
  // Mutations
  const verifyVendorMutation = useVerifyVendor({
    onSuccess: () => {
      success('Vendor verified successfully!');
      setShowVerificationModal(false);
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to verify vendor');
    }
  });

  const rejectVendorMutation = useRejectVendor({
    onSuccess: () => {
      success('Vendor rejected successfully!');
      setShowRejectionModal(false);
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to reject vendor');
    }
  });

  const updateVendorProfileMutation = useUpdateVendorProfile({
    onSuccess: () => {
      success('Vendor profile updated successfully!');
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update vendor profile');
    }
  });

  const suspendVendorMutation = useSuspendVendor({
    onSuccess: (_, variables) => {
      const msg = variables.isActive ? 'Vendor reactivated successfully! They have been notified by email.' : 'Vendor suspended. They have been logged out and notified by email.';
      success(msg);
      setShowSuspendModal(false);
      setSuspendReason('');
      refetchVendor();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update vendor status');
    }
  });

  const connectDisconnectMutation = useMutation({
    mutationFn: async (vendorProfileId) =>
      postAdminVendorConnectDisconnect(vendorProfileId, { delete_remote: true }),
    onSuccess: (res) => {
      success(res?.message || 'Stripe Connect unlinked.');
      refetchVendor();
      queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
    },
    onError: (error) => {
      showError(error?.message || error?.response?.data?.message || 'Could not unlink Connect');
    },
  });

  // Extract data
  const vendor = vendorData?.data;
  const vendorProfile = vendor?.vendor_profile;
  const stripeConnect = vendorProfile?.stripe_connect;
  const vendorSubscriptions = useMemo(() => vendor?.vendor_subscriptions || [], [vendor?.vendor_subscriptions]);
  const userVerifications = vendor?.user_verifications || [];
  const error = vendorError?.message || null;

  // Handle vendor loading errors (omit showError from deps: ToastProvider passes a new function each render)
  useEffect(() => {
    if (!vendorError) return;
    const msg =
      vendorError?.response?.data?.message ||
      vendorError?.message ||
      'Failed to load vendor details';
    showError(msg);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable toast once per vendorError
  }, [vendorError]);

  // Handle verification
  const handleVerify = () => {
    verifyVendorMutation.mutate({
      id,
      data: verificationData
    });
  };

  // Handle rejection
  const handleReject = () => {
    rejectVendorMutation.mutate({
      id,
      data: rejectionData
    });
  };

  // Handle featured status toggle
  const handleToggleFeatured = () => {
    updateVendorProfileMutation.mutate({
      id,
      data: {
        is_featured: !vendorProfile?.is_featured
      }
    });
  };

  // Handle active status toggle
  const handleToggleActive = () => {
    updateVendorProfileMutation.mutate({
      id,
      data: {
        is_active: !vendor?.is_active
      }
    });
  };

  const handleSuspend = () => {
    const isActive = vendor?.is_active !== false && vendorProfile?.is_active !== false;
    suspendVendorMutation.mutate({
      id,
      isActive: !isActive,
      reason: isActive ? suspendReason : null
    });
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading vendor details…</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-4">{error || 'Vendor not found'}</p>
          <Button className="!rounded-xl" onClick={() => navigate('/admin/vendors')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Vendors
          </Button>
        </div>
      </div>
    );
  }

  const ordersCount = vendorProfile?.bookings_count ?? 0;

  const tabGroups = [
    {
      label: 'Profile',
      tabs: [
        { id: 'overview', label: 'Overview', icon: Eye },
        { id: 'verification', label: 'Verification', icon: Shield },
      ],
    },
    {
      label: 'Catalog',
      tabs: [
        { id: 'services', label: 'Services', icon: Package },
        { id: 'packages', label: 'Packages', icon: Gift },
        { id: 'services_category', label: 'Categories', icon: Camera },
        { id: 'portfolio', label: 'Portfolio', icon: FileText },
      ],
    },
    {
      label: 'Operations',
      tabs: [
        { id: 'orders', label: `Orders (${ordersCount})`, icon: ShoppingBag },
        { id: 'analytics', label: 'Analytics', icon: DollarSign },
      ],
    },
  ];

  const allTabs = tabGroups.flatMap((g) => g.tabs);

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={vendorProfile?.business_name || vendor?.name || 'Vendor'}
        subtitle="Review profile, catalog, bookings, invoices, and contracts for this vendor."
        actions={
          <Link
            to="/admin/vendors"
            className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-2 text-sm font-medium !rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            Back to Vendors
          </Link>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.vendorDetail.title}
        steps={ADMIN_PAGE_GUIDES.vendorDetail.steps}
        icon={ListChecks}
      />

      <VendorHeader
        vendor={vendor}
        vendorProfile={vendorProfile}
        onVerify={() => setShowVerificationModal(true)}
        onReject={() => setShowRejectionModal(true)}
        onSuspend={() => setShowSuspendModal(true)}
      />

      <StatsGrid 
        stats={[
          {
            key: 'orders',
            title: 'Orders',
            value: ordersCount,
            icon: ShoppingBag,
            variant: 'info',
            onClick: () => setActiveTab('orders')
          },
          {
            key: 'verification',
            title: 'Verification',
            value: vendorProfile?.is_verified ? 'Verified' : 'Pending',
            icon: Shield,
            variant: vendorProfile?.is_verified ? 'success' : 'warning'
          },
          {
            key: 'rating',
            title: 'Rating',
            value: vendorProfile?.average_rating ? `${vendorProfile.average_rating}/5.0` : 'N/A',
            icon: Star,
            variant: 'default'
          },
          {
            key: 'connect',
            title: 'Stripe Connect',
            value: stripeConnect?.account_id
              ? (stripeConnect.is_payout_ready
                  ? 'Payout ready'
                  : (stripeConnect.onboarding_status || 'Pending').replace(/_/g, ' '))
              : 'Not linked',
            icon: CreditCard,
            variant: stripeConnect?.is_payout_ready
              ? 'success'
              : stripeConnect?.account_id
                ? 'warning'
                : 'default',
          },
        ]}
        columns={4}
      />

      <div className="rounded-2xl border border-violet-200/80 dark:border-violet-900/50 bg-violet-50/40 dark:bg-violet-950/20 p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-violet-700 dark:text-violet-300" />
              <h3 className="text-sm font-semibold text-violet-900 dark:text-violet-100">
                Stripe Connect
              </h3>
              <StripeConnectStatusBadge connect={stripeConnect} />
              {stripeConnect?.is_test_mode ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300">
                  Test mode
                </span>
              ) : null}
            </div>
            {stripeConnect?.account_id ? (
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-violet-600/80 dark:text-violet-400">Account</dt>
                  <dd className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all">
                    {stripeConnect.account_id}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-violet-600/80 dark:text-violet-400">Status</dt>
                  <dd className="capitalize text-gray-900 dark:text-gray-100">
                    {(stripeConnect.onboarding_status || 'pending').replace(/_/g, ' ')}
                    {' · '}
                    Payout ready: {stripeConnect.is_payout_ready ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-violet-600/80 dark:text-violet-400">Capabilities</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    Charges {stripeConnect.charges_enabled ? 'on' : 'off'}
                    {' · '}
                    Payouts {stripeConnect.payouts_enabled ? 'on' : 'off'}
                    {' · '}
                    Details {stripeConnect.details_submitted ? 'submitted' : 'incomplete'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-violet-600/80 dark:text-violet-400">Payout profile</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    {[stripeConnect.payout_country, stripeConnect.payout_currency]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                This vendor has not linked a Stripe Express account yet.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Link
              to={`/admin/vendor-payouts?vendor_id=${vendorProfile?.id || ''}`}
              className="inline-flex items-center gap-2 px-3 py-2 !rounded-xl border border-violet-200 bg-white dark:bg-gray-900 text-sm font-medium text-violet-800 dark:text-violet-200 hover:bg-violet-50 dark:hover:bg-violet-950/40"
            >
              <Banknote className="w-4 h-4" />
              Open payouts
            </Link>
            {stripeConnect?.account_id ? (
              <button
                type="button"
                disabled={connectDisconnectMutation.isPending || !vendorProfile?.id}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Unlink and delete this vendor’s Stripe Connect account so they can start over?'
                    )
                  ) {
                    return;
                  }
                  connectDisconnectMutation.mutate(vendorProfile.id);
                }}
                className="inline-flex items-center gap-2 px-3 py-2 !rounded-xl border border-red-200 bg-white dark:bg-gray-900 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50"
              >
                {connectDisconnectMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Reset Connect
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => setActiveTab('orders')}
          className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 px-4 py-3 text-left shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              <ShoppingBag className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">Bookings</p>
              <p className="text-xs text-gray-500">{ordersCount} marketplace orders</p>
            </div>
          </div>
        </button>
        <Link
          to={`/admin/accounting?vendor_id=${vendorProfile?.id || ''}`}
          className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 px-4 py-3 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              <BookOpen className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">Accounting</p>
              <p className="text-xs text-gray-500">Ledger statement</p>
            </div>
          </div>
        </Link>
        <Link
          to={`/admin/vendor-payouts?vendor_id=${vendorProfile?.id || ''}`}
          className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 px-4 py-3 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <Banknote className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">Payouts</p>
              <p className="text-xs text-gray-500">Open vendor payouts</p>
            </div>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setActiveTab('verification')}
          className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 px-4 py-3 text-left shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              <Shield className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">Verification</p>
              <p className="text-xs text-gray-500">
                {vendorProfile?.is_verified ? 'Verified business' : 'Review documents'}
              </p>
            </div>
          </div>
        </button>
      </div>

      <VendorDocumentsHub vendorUserId={id} />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5 space-y-3">
          {tabGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                {group.label}
              </p>
              <div
                className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
                role="tablist"
                aria-label={group.label}
              >
                {group.tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 sm:p-5">
            {activeTab === 'overview' && (
              <OverviewTab
                vendor={vendor}
                vendorProfile={vendorProfile}
                vendorSubscriptions={vendorSubscriptions}
                planUsage={vendor?.plan_usage}
                formatDate={formatDate}
                onToggleFeatured={handleToggleFeatured}
                onToggleActive={handleToggleActive}
                isUpdatingFeatured={updateVendorProfileMutation.isPending}
                isUpdatingActive={updateVendorProfileMutation.isPending}
              />
            )}

            {activeTab === 'orders' && (
              <OrdersTab vendorProfile={vendorProfile} />
            )}

            {activeTab === 'services' && (
              <ServicesTab vendorProfile={vendorProfile} />
            )}

            {activeTab === 'packages' && (
              <PackagesTab vendorProfile={vendorProfile} onUpdate={refetchVendor} />
            )}

            {activeTab === 'services_category' && (
              <ServicesCategoryTab vendorProfile={vendorProfile} />
            )}

            {activeTab === 'portfolio' && (
              <PortfolioTab vendorProfile={vendorProfile} />
            )}

            {activeTab === 'verification' && (
              <VerificationTab
                vendor={vendor}
                vendorProfile={vendorProfile}
                userVerifications={userVerifications}
                formatDate={formatDate}
              />
            )}

            {activeTab === 'analytics' && (
              <AnalyticsTab vendor={vendor} vendorProfile={vendorProfile} />
            )}

            {!allTabs.some((t) => t.id === activeTab) && (
              <OverviewTab
                vendor={vendor}
                vendorProfile={vendorProfile}
                vendorSubscriptions={vendorSubscriptions}
                planUsage={vendor?.plan_usage}
                formatDate={formatDate}
                onToggleFeatured={handleToggleFeatured}
                onToggleActive={handleToggleActive}
                isUpdatingFeatured={updateVendorProfileMutation.isPending}
                isUpdatingActive={updateVendorProfileMutation.isPending}
              />
            )}
          </div>
        </div>

        {/* Verification Modal */}
        {showVerificationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Verify Vendor</h3>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Notes
                  </label>
                  <textarea
                    value={verificationData.verification_notes}
                    onChange={(e) => setVerificationData({...verificationData, verification_notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Add verification notes..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => setShowVerificationModal(false)}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleVerify}
                    disabled={verifyVendorMutation.isPending}
                    loading={verifyVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-green-600 text-green-600 hover:bg-green-50"
                  >
                    {verifyVendorMutation.isPending ? 'Verifying...' : 'Verify Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? 'Suspend Vendor' : 'Reactivate Vendor'}
                </h3>
                {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      The vendor will be logged out immediately and cannot log in until reactivated. An email notification will be sent.
                    </p>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                      <textarea
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Reason for suspension (will be included in email)..."
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 mb-6">
                    The vendor will be able to log in and access their account again.
                  </p>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSuspend}
                    disabled={suspendVendorMutation.isPending}
                    loading={suspendVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className={`w-full sm:w-auto rounded-full ${
                      (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'border-amber-600 text-amber-600 hover:bg-amber-50'
                        : 'border-green-600 text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {suspendVendorMutation.isPending
                      ? 'Processing...'
                      : (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'Suspend Vendor'
                        : 'Reactivate Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? 'Suspend Vendor' : 'Reactivate Vendor'}
                </h3>
                {(vendor?.is_active !== false && vendorProfile?.is_active !== false) ? (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      The vendor will be logged out immediately and cannot log in until reactivated. An email notification will be sent.
                    </p>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                      <textarea
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Reason for suspension (will be included in email)..."
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 mb-6">
                    The vendor will be able to log in and access their account again.
                  </p>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSuspend}
                    disabled={suspendVendorMutation.isPending}
                    loading={suspendVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className={`w-full sm:w-auto rounded-full ${
                      (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'border-amber-600 text-amber-600 hover:bg-amber-50'
                        : 'border-green-600 text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {suspendVendorMutation.isPending
                      ? 'Processing...'
                      : (vendor?.is_active !== false && vendorProfile?.is_active !== false)
                        ? 'Suspend Vendor'
                        : 'Reactivate Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {showRejectionModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Vendor</h3>
                
                <div className="mb-4">
                  <SearchSelect
                    label="Rejection Type"
                    value={rejectionData.rejection_type}
                    onChange={(value) => setRejectionData({...rejectionData, rejection_type: value})}
                    options={[
                      { value: 'document_issue', label: 'Document Issue' },
                      { value: 'background_check', label: 'Background Check' },
                      { value: 'other', label: 'Other' }
                    ]}
                    placeholder="Select rejection type..."
                    required={true}
                    size="sm"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason
                  </label>
                  <textarea
                    value={rejectionData.rejection_reason}
                    onChange={(e) => setRejectionData({...rejectionData, rejection_reason: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Explain why the vendor is being rejected..."
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <Button
                    onClick={() => setShowRejectionModal(false)}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-gray-600 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={rejectVendorMutation.isPending || !rejectionData.rejection_reason}
                    loading={rejectVendorMutation.isPending}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto rounded-full border-red-600 text-red-600 hover:bg-red-50"
                  >
                    {rejectVendorMutation.isPending ? 'Rejecting...' : 'Reject Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AdminVendorDetails;
